import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Text } from '@cuemath/web-comps';
import {
  useGetSupportTeams,
  invalidateSupportTeams,
  usePatchSupportTeam,
  useGetAgents,
  invalidateAgents,
  usePatchAgentDetails,
} from 'actions/support-agent';
import {
  GENERAL_TEAM_NAME,
  GENERAL_RENAME_TOOLTIP,
  GENERAL_DELETE_TOOLTIP,
  GENERAL_ADD_AGENT_TOOLTIP,
  GENERAL_REMOVE_AGENT_TOOLTIP,
  ROLE_LABELS,
  AGENT_ACTIONS,
  CACHE_KEYS,
  ERROR_AUTO_CLEAR_MS,
} from '../../constants';
import {
  TeamsHeaderActions,
  TeamHeaderBtn,
  TeamsList,
  TeamCard,
  TeamHeader,
  TeamExpandBtn,
  TeamExpandChev,
  TeamName,
  TeamNameInput,
  TeamCount,
  TeamActions,
  TeamActionBtn,
  TeamBody,
  TeamAgentsHeader,
  TeamAgentsLabel,
  TeamAgentsList,
  TeamAgentItem,
  TeamAgentAvatar,
  AgentOnlineDot,
  TeamAgentInfo,
  TeamAgentName,
  TeamAgentEmail,
  TeamAgentMeta,
  TeamAgentTickets,
  TeamEmpty,
  AgentRoleBadge,
  WarningText,
  ConfirmText,
  ConfirmBoldName,
  BtnDanger,
  InlineRenameError,
  TeamAddAgentBtn,
} from './styled';
import {
  ModalOverlay,
  ModalCard,
  ModalHead,
  ModalBody,
  ModalFoot,
  BtnSecondary,
  IconBtn,
  ModalErrorMsg,
} from '../../styled-comps';
import Icon from '../icon';
import AddAgentModal from './add-agent-modal';
import CreateTeamModal from './create-team-modal';
import AddAgentToTeamModal from './add-agent-to-team-modal';
import RemoveAgentModal from './remove-agent-modal';
import EditTeamsModal from './edit-teams-modal';
import ChangeRoleModal from './change-role-modal';
import LastTeamDecisionDialog from './last-team-decision-dialog';
import AgentRowMenu from './agent-row-menu';

const TeamsView = () => {
  const [expandedTeamId, setExpandedTeamId] = useState(null);
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [renameError, setRenameError] = useState(null);

  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [showAddAgent, setShowAddAgent] = useState(false);
  const [showRemoveAgent, setShowRemoveAgent] = useState(false);

  const [addAgentToTeam, setAddAgentToTeam] = useState(null);
  const [agentToRemove, setAgentToRemove] = useState(null); // {team, agent}
  const [agentRemoveError, setAgentRemoveError] = useState(null);

  const [agentToEditTeams, setAgentToEditTeams] = useState(null);
  const [agentToChangeRole, setAgentToChangeRole] = useState(null);
  const [lastTeamDecision, setLastTeamDecision] = useState(null); // agent

  const [teamToDelete, setTeamToDelete] = useState(null);
  const [teamDeleteError, setTeamDeleteError] = useState(null);

  const {
    data: teamsData,
    get: getTeams,
    isProcessing: teamsLoading,
    isProcessingFailed: teamsFailed,
    isStale: teamsStale,
  } = useGetSupportTeams();

  const {
    data: agentsData,
    get: getAgents,
    isProcessing: agentsLoading,
    isProcessingFailed: agentsFailed,
    isStale: agentsStale,
  } = useGetAgents();

  const { patch: patchTeam, isProcessing: patchingTeam } = usePatchSupportTeam({
    onComplete: (error, data) => {
      if (error) {
        if (deletingTeamRef.current) {
          setTeamDeleteError(typeof error === 'string' ? error : 'Failed to delete team');
        } else {
          setRenameError(typeof error === 'string' ? error : 'Failed to rename team');
        }
        deletingTeamRef.current = false;
        return;
      }
      invalidateSupportTeams(CACHE_KEYS.SUPPORT_TEAMS);
      invalidateAgents(CACHE_KEYS.AGENTS);
      if (deletingTeamRef.current) {
        setTeamToDelete(null);
        deletingTeamRef.current = false;
      } else {
        setEditingTeamId(null);
        setEditingName('');
      }
    },
  });

  const { patch: patchAgent, isProcessing: patchingAgent } = usePatchAgentDetails({
    onComplete: error => {
      if (error) {
        setAgentRemoveError(
          typeof error === 'string' ? error : 'Failed to remove agent from team',
        );
        return;
      }
      invalidateAgents(CACHE_KEYS.AGENTS);
      setAgentToRemove(null);
    },
  });

  // We share `patchTeam` between rename and delete; this ref tracks which
  // operation is in flight so the onComplete handler routes errors correctly.
  const deletingTeamRef = useRef(false);

  useEffect(() => {
    getTeams(CACHE_KEYS.SUPPORT_TEAMS);
    getAgents(CACHE_KEYS.AGENTS);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (teamsStale && !teamsLoading) getTeams(CACHE_KEYS.SUPPORT_TEAMS);
  }, [teamsStale, teamsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (agentsStale && !agentsLoading) getAgents(CACHE_KEYS.AGENTS);
  }, [agentsStale, agentsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!renameError) return undefined;
    const t = setTimeout(() => setRenameError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(t);
  }, [renameError]);

  useEffect(() => {
    if (!teamDeleteError) return undefined;
    const t = setTimeout(() => setTeamDeleteError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(t);
  }, [teamDeleteError]);

  useEffect(() => {
    if (!agentRemoveError) return undefined;
    const t = setTimeout(() => setAgentRemoveError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(t);
  }, [agentRemoveError]);

  const rawTeams = teamsData?.teams || [];
  const teams = [
    ...rawTeams.filter(t => t.name !== GENERAL_TEAM_NAME),
    ...rawTeams.filter(t => t.name === GENERAL_TEAM_NAME),
  ];

  const agents = agentsData?.agents || [];

  const startRename = useCallback(team => {
    setEditingTeamId(team.id);
    setEditingName(team.name);
    setRenameError(null);
  }, []);

  const cancelRename = useCallback(() => {
    setEditingTeamId(null);
    setEditingName('');
    setRenameError(null);
  }, []);

  const closeRemoveAgent = useCallback(() => {
    setAgentToRemove(null);
    setAgentRemoveError(null);
  }, []);

  const closeDeleteTeam = useCallback(() => {
    setTeamToDelete(null);
    setTeamDeleteError(null);
  }, []);

  const submitRename = useCallback(() => {
    const trimmed = editingName.trim();
    if (!trimmed || !editingTeamId || patchingTeam) return;
    deletingTeamRef.current = false;
    patchTeam(editingTeamId, { name: trimmed });
  }, [editingName, editingTeamId, patchingTeam, patchTeam]);

  const confirmDeleteTeam = useCallback(() => {
    if (!teamToDelete || patchingTeam) return;
    deletingTeamRef.current = true;
    patchTeam(teamToDelete.id, { is_deleted: true });
  }, [teamToDelete, patchingTeam, patchTeam]);

  const confirmRemoveAgentFromTeam = useCallback(() => {
    if (!agentToRemove || patchingAgent) return;
    const { team, agent } = agentToRemove;
    const nextTeamIds = (agent.teams || [])
      .filter(t => t.name !== GENERAL_TEAM_NAME && t.id !== team.id)
      .map(t => t.id);
    patchAgent(agent.user_id, {
      action: AGENT_ACTIONS.UPDATE_TEAMS,
      team_ids: nextTeamIds,
    });
  }, [agentToRemove, patchingAgent, patchAgent]);

  const teamAgents = teamId => agents.filter(a => a.teams?.some(t => t.id === teamId));

  const handleRowRemoveClick = useCallback((team, agent) => {
    const specificCount = (agent.teams || []).filter(t => t.name !== GENERAL_TEAM_NAME)
      .length;
    if (specificCount <= 1) {
      setLastTeamDecision(agent);
    } else {
      setAgentToRemove({ team, agent });
    }
  }, []);

  const initialLoading =
    (teamsLoading && teams.length === 0) || (agentsLoading && agents.length === 0);
  const fetchFailed = teamsFailed && teams.length === 0;

  return (
    <>
      <TeamsHeaderActions>
        <TeamHeaderBtn onClick={() => setShowAddAgent(true)}>
          <Icon name="user" size={14} />
          Add Agent
        </TeamHeaderBtn>
        <TeamHeaderBtn onClick={() => setShowRemoveAgent(true)}>
          <Icon name="close" size={14} />
          Remove Agent
        </TeamHeaderBtn>
        <TeamHeaderBtn $primary onClick={() => setShowCreateTeam(true)}>
          New Team
        </TeamHeaderBtn>
      </TeamsHeaderActions>

      <TeamsList>
        {initialLoading && <TeamEmpty>Loading…</TeamEmpty>}
        {!initialLoading && fetchFailed && (
          <WarningText>Failed to load teams.</WarningText>
        )}
        {!initialLoading && agentsFailed && agents.length === 0 && (
          <WarningText>Failed to load agents.</WarningText>
        )}

        {!initialLoading && !fetchFailed && teams.length === 0 && (
          <TeamEmpty>No teams yet — create one to start routing.</TeamEmpty>
        )}

        {!initialLoading &&
          teams.map(team => {
            const isExpanded = expandedTeamId === team.id;
            const isEditing = editingTeamId === team.id;
            const isGeneral = team.name === GENERAL_TEAM_NAME;
            const members = teamAgents(team.id);

            return (
              <TeamCard key={team.id}>
                <TeamHeader>
                  {isEditing ? (
                    <>
                      <TeamNameInput
                        autoFocus
                        value={editingName}
                        onChange={e => setEditingName(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') submitRename();
                          else if (e.key === 'Escape') cancelRename();
                        }}
                      />
                      <TeamActions>
                        <TeamActionBtn
                          onClick={submitRename}
                          disabled={!editingName.trim() || patchingTeam}
                          title="Save"
                        >
                          <Icon name="check" size={14} />
                        </TeamActionBtn>
                        <TeamActionBtn
                          onClick={cancelRename}
                          disabled={patchingTeam}
                          title="Cancel"
                        >
                          <Icon name="close" size={14} />
                        </TeamActionBtn>
                      </TeamActions>
                    </>
                  ) : (
                    <>
                      <TeamExpandBtn
                        onClick={() => setExpandedTeamId(isExpanded ? null : team.id)}
                      >
                        <TeamExpandChev $expanded={isExpanded}>
                          <Icon name="chev" size={16} />
                        </TeamExpandChev>
                        <TeamName>{team.name}</TeamName>
                        <TeamCount>
                          {members.length} agent{members.length === 1 ? '' : 's'}
                        </TeamCount>
                      </TeamExpandBtn>
                      <TeamActions>
                        <TeamActionBtn
                          onClick={() => startRename(team)}
                          disabled={isGeneral}
                          title={isGeneral ? GENERAL_RENAME_TOOLTIP : 'Rename team'}
                        >
                          <Icon name="edit" size={14} />
                        </TeamActionBtn>
                        <TeamActionBtn
                          $danger
                          onClick={() => setTeamToDelete(team)}
                          disabled={isGeneral}
                          title={isGeneral ? GENERAL_DELETE_TOOLTIP : 'Delete team'}
                        >
                          <Icon name="trash" size={14} />
                        </TeamActionBtn>
                      </TeamActions>
                    </>
                  )}
                </TeamHeader>

                {isEditing && renameError && (
                  <InlineRenameError>{renameError}</InlineRenameError>
                )}

                {isExpanded && !isEditing && (
                  <TeamBody>
                    <TeamAgentsHeader>
                      <TeamAgentsLabel>Agents</TeamAgentsLabel>
                      <TeamAddAgentBtn
                        onClick={() => setAddAgentToTeam(team)}
                        disabled={isGeneral}
                        title={isGeneral ? GENERAL_ADD_AGENT_TOOLTIP : undefined}
                      >
                        Add Agent
                      </TeamAddAgentBtn>
                    </TeamAgentsHeader>

                    {members.length === 0 ? (
                      <TeamEmpty>No agents in this team</TeamEmpty>
                    ) : (
                      <TeamAgentsList>
                        {members.map(agent => (
                          <TeamAgentItem key={agent.user_id}>
                            <TeamAgentAvatar>
                              {(agent.name || '--').slice(0, 2).toUpperCase()}
                              <AgentOnlineDot $online={agent.is_online} />
                            </TeamAgentAvatar>
                            <TeamAgentInfo>
                              <TeamAgentName>{agent.name}</TeamAgentName>
                              {agent.email && (
                                <TeamAgentEmail>{agent.email}</TeamAgentEmail>
                              )}
                              <TeamAgentMeta>
                                <AgentRoleBadge $role={agent.role}>
                                  {ROLE_LABELS[agent.role] || agent.role}
                                </AgentRoleBadge>
                                {typeof agent.open_ticket_count === 'number' && (
                                  <TeamAgentTickets>
                                    {agent.open_ticket_count} ticket
                                    {agent.open_ticket_count === 1 ? '' : 's'}
                                  </TeamAgentTickets>
                                )}
                              </TeamAgentMeta>
                            </TeamAgentInfo>
                            <AgentRowMenu
                              disabled={isGeneral}
                              items={[
                                {
                                  label: 'Edit teams',
                                  onClick: () => setAgentToEditTeams(agent),
                                },
                                {
                                  label: 'Change role',
                                  onClick: () => setAgentToChangeRole(agent),
                                },
                              ]}
                            />
                            <TeamActionBtn
                              onClick={() => handleRowRemoveClick(team, agent)}
                              disabled={isGeneral}
                              title={
                                isGeneral
                                  ? GENERAL_REMOVE_AGENT_TOOLTIP
                                  : 'Remove from team'
                              }
                            >
                              <Icon name="close" size={14} />
                            </TeamActionBtn>
                          </TeamAgentItem>
                        ))}
                      </TeamAgentsList>
                    )}
                  </TeamBody>
                )}
              </TeamCard>
            );
          })}
      </TeamsList>

      <CreateTeamModal open={showCreateTeam} onClose={() => setShowCreateTeam(false)} />

      <AddAgentModal
        open={showAddAgent}
        onClose={() => setShowAddAgent(false)}
        existingAgentIds={agents.map(a => a.user_id)}
      />

      <RemoveAgentModal
        open={showRemoveAgent}
        agents={agents}
        onClose={() => setShowRemoveAgent(false)}
      />

      {addAgentToTeam && (
        <AddAgentToTeamModal
          team={addAgentToTeam}
          agents={agents}
          onClose={() => setAddAgentToTeam(null)}
        />
      )}

      {agentToEditTeams && (
        <EditTeamsModal
          agent={agentToEditTeams}
          onClose={() => setAgentToEditTeams(null)}
        />
      )}

      {agentToChangeRole && (
        <ChangeRoleModal
          agent={agentToChangeRole}
          onClose={() => setAgentToChangeRole(null)}
        />
      )}

      {lastTeamDecision && (
        <LastTeamDecisionDialog
          agent={lastTeamDecision}
          onCancel={() => setLastTeamDecision(null)}
          onCompleted={() => setLastTeamDecision(null)}
        />
      )}

      {agentToRemove && (
        <ModalOverlay onClick={closeRemoveAgent}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalHead>
              <Text variant="body1Medium">Remove Agent from Team</Text>
              <IconBtn onClick={closeRemoveAgent}>
                <Icon name="close" size={16} />
              </IconBtn>
            </ModalHead>
            <ModalBody>
              {agentRemoveError && <ModalErrorMsg>{agentRemoveError}</ModalErrorMsg>}
              <ConfirmText>
                Remove <ConfirmBoldName>{agentToRemove.agent.name}</ConfirmBoldName> from{' '}
                <ConfirmBoldName>{agentToRemove.team.name}</ConfirmBoldName>?
              </ConfirmText>
            </ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={closeRemoveAgent}>Cancel</BtnSecondary>
              <BtnDanger onClick={confirmRemoveAgentFromTeam} disabled={patchingAgent}>
                {patchingAgent ? 'Removing...' : 'Remove'}
              </BtnDanger>
            </ModalFoot>
          </ModalCard>
        </ModalOverlay>
      )}

      {teamToDelete && (
        <ModalOverlay onClick={closeDeleteTeam}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalHead>
              <Text variant="body1Medium">Delete Team</Text>
              <IconBtn onClick={closeDeleteTeam}>
                <Icon name="close" size={16} />
              </IconBtn>
            </ModalHead>
            <ModalBody>
              {teamDeleteError && <ModalErrorMsg>{teamDeleteError}</ModalErrorMsg>}
              <ConfirmText>
                Delete team <ConfirmBoldName>{teamToDelete.name}</ConfirmBoldName>? Agents
                stay; only the team grouping is removed.
              </ConfirmText>
            </ModalBody>
            <ModalFoot>
              <BtnSecondary onClick={closeDeleteTeam}>Cancel</BtnSecondary>
              <BtnDanger onClick={confirmDeleteTeam} disabled={patchingTeam}>
                {patchingTeam ? 'Deleting...' : 'Delete Team'}
              </BtnDanger>
            </ModalFoot>
          </ModalCard>
        </ModalOverlay>
      )}
    </>
  );
};

export default TeamsView;
